import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { formatPartySubtitle } from './partySubtitle';
import { usePOLineDetail } from './usePOLineDetail';
import POLineDetailTabs from './POLineDetailTabs';
import { POLineDetailTabContent } from './POLineDetailTab';
import { POLineDetailFooter } from './POLineDetailFooter';
import OTIFReviewActions from './OTIFReviewActions';
import {
  OtifDecisionBadge, otifDecisionOf, otifDecisionAudit, otifDecisionReason,
} from './otifDecision';
import { ConcessionStatusBadge } from './ConcessionStatusBadge';
import POLineHeaderBadges from './POLineHeaderBadges';
import { deliveredPercentOf } from './deliveredQty';
import { POLineDetailDialogs } from './POLineDetailDialogs';
import { canReleaseHold, lineIsFinished } from './tabs/po-lines/utils';
import { placerSideOf, viewerSideOf } from './lineStatus';
import { formatAmount } from '../../lib/amountDisplay';
import { formatUnitPrice } from '../../lib/unitPrice';
import { ncrAuditEntries } from './ncrAuditEvents';
import { canActOnProposal, mayCloseDeclinedRequest } from './pendingProposal';
import type { ConcessionRequestType } from '../../lib/api/types/quality';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';

interface POLineDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineId: number;
  userType?: 'customer' | 'supplier';
  initialAction?: 'propose_change' | null;
  initialTab?: string;
  onUpdate?: () => void;
}

export default function POLineDetailModal({ isOpen, onClose, lineId, userType = 'customer', initialAction = null, initialTab, onUpdate }: POLineDetailModalProps) {
  const { user } = useAuth();
  // Which side this user is ACTING as — a company that runs both portals has one
  // of each, and the server judges by the portal, not by the profile (attacker
  // chain 2, round 3). The operator is neither and `viewerSideOf` says so.
  const { isSupplier: portalIsSupplier } = usePortalMode();
  const actingSide = portalIsSupplier ? 'supplier' as const : 'customer' as const;
  const { hasPurchasingWriteAccess } = useUserPermissions();
  const { isFeatureEnabled, isConcessionEnabledForCustomer } = useMenuSettings();
  // When the tenant uses schedule-line collaboration, PO-line-level
  // Accept / Decline / Propose are suppressed — demand lives on
  // POLineSchedule rows and negotiation belongs in the Schedule modal.
  // Keeping them visible here meant DE users could propose changes
  // against the PO-line header that never flowed back to any schedule,
  // creating two disconnected audit trails for the same logical ask.
  const scheduleCollaborationActive = isFeatureEnabled('po_line_schedules');

  // The modal's data and actions live in usePOLineDetail; what stays here is
  // presentational state only.
  const {
    line,
    changelogs,
    loading,
    error,
    grnCount,
    ncrs,
    asnCount,
    unreadMessageCount,
    refreshUnreadMessageCount,
    fetchLineDetails,
    getChangeCount,
    handleToggleCritical,
    handleToggleHold,
    handleSetDates,
    handleCloseDeclinedRequest,
    handleWorkflowAction,
    canShowWorkflowActions,
    canShowProposeChange,
  } = usePOLineDetail({
    lineId,
    isOpen,
    userType,
    user,
    scheduleCollaborationActive,
    hasPurchasingWriteAccess,
    onUpdate,
  });

  /**
   * P10: may this viewer write the three dates outright, here and now?
   *
   * Lifted out of the footer's props (walkthrough batch 3, Aaron 17 Sep 2026)
   * because the AUTO-OPEN below needs the same answer. Mirrors
   * `POLineWorkflowService.set_dates_at_new`, which is the real gate; every
   * clause here is cosmetic and has to agree with it or the screen offers a
   * control that 400s.
   */
  const maySetDatesDirectly = !!line
    && userType === 'customer'
    && line.acknowledge_status === 'new'
    && line.status !== 'received'
    && !line.on_hold
    && !scheduleCollaborationActive
    && hasPurchasingWriteAccess(line.customer_id);

  // Customer review 11 Sep (item 30a): NCRs join the Detail tab's Audit
  // History. Only there — getChangeCount and the Change Requests tab keep
  // reading the real change log.
  const detailAuditEntries = useMemo(
    () => (ncrs ? [...changelogs, ...ncrAuditEntries(ncrs)] : changelogs),
    [changelogs, ncrs],
  );

  // Workflow modal state
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  // RETIRED 17 Sep 2026 (walkthrough batch 3): the Change Requests tab's
  // second Accept / Reject pair was this modal's only caller, and it is gone —
  // a decline now runs per field, with the counter, from the request row
  // itself. DeclineDateModal itself stays: the PO Lines grid still opens it.
  const [proposeModalOpen, setProposeModalOpen] = useState(false);
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  // P10: the customer's direct date edit, only while the line is `new`.
  const [setDatesModalOpen, setSetDatesModalOpen] = useState(false);
  // CR-5551: which deviation modal (if any) is open. Null = closed.
  // Same modal component handles both PE and CE, distinguished by the
  // requestType state.
  const [deviationRequestType, setDeviationRequestType] = useState<ConcessionRequestType | null>(null);
  const [modalTab, setModalTab] = useState(initialTab || 'detail');


  // CR-3400: Auto-open propose change modal when initialAction is set.
  // CR-4680 round 6: must only fire once per mount. Without the ref guard the
  // effect re-fires whenever `line` updates (e.g. fetchLineDetails reloading
  // after a cancel-change call), which re-opens the Propose Change modal
  // after the user cancels a change request. The parent unmounts this modal
  // on close (selectedLineId → null), so the ref resets naturally.
  //
  // Walkthrough batch 3 (Aaron, 17 Sep 2026): *"the update dates modal is still
  // not there."* The button WAS there — but the grid's blue pencil is the way
  // anyone actually reaches this, and it arrives carrying
  // `initialAction='propose_change'`, so the Propose Change dialog opened over
  // the top and the new control was never seen. Adding a second door and
  // leaving the first one pointing at the old room is not a fix.
  //
  // ONE entry point, the status picks the dialog — which is legacy's own shape:
  // `OpenOrders.js:1961` sends the same control to `ProposeChange4New` when the
  // line is New and the caller is the customer, and to the proposal flow
  // otherwise.
  const proposeAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (line && initialAction === 'propose_change' && !proposeAutoOpenedRef.current) {
      proposeAutoOpenedRef.current = true;
      if (maySetDatesDirectly) {
        setSetDatesModalOpen(true);
      } else {
        setProposeModalOpen(true);
      }
    }
  }, [line, initialAction, maySetDatesDirectly]);

  const { formatDate: contextFormatDate, formatQuantity, preferences } = useFormatters();

  // CR-4707: PO Line pop-up monetary fields consult the admin-configurable
  // unit_price_decimals setting instead of the legacy `showDecimals: true`
  // shortcut which always meant 3.
  //
  // Batch 4 (Aaron, 17 Sep 2026): and they carry NO currency symbol. The line
  // has a Currency field of its own two boxes away, and a figure with a glyph
  // or a thousands separator in it stops being a number the moment anyone
  // exports it. See lib/amountDisplay for the whole reasoning; the currency
  // argument is dropped here because there is no symbol left to choose.
  const formatCurrency = (value: string) =>
    formatAmount(value, preferences.unit_price_decimals);
  // The unit price alone keeps every decimal it has (lib/unitPrice).
  const formatLineUnitPrice = (value: string) =>
    formatUnitPrice(value, preferences.unit_price_decimals);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  if (!isOpen) return null;

  // CR-7258: customer before supplier, and empty when neither is on the
  // payload -- both are optional on PurchaseOrderLine, so this can be a
  // genuinely empty string and the subtitle is then not rendered at all.
  const lineParties = formatPartySubtitle(line?.customer_name, line?.supplier_name);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[95vh] flex flex-col" style={{ maxWidth: '1400px', minWidth: '1200px' }}>
        {/* Header — CR-4703: status badges only, actions moved to fixed footer */}
        <div className="flex items-center justify-between p-5 border-b bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-primary">
                PO: {line?.po_number} / Line: {line?.line_number}
              </h2>
              {/* CR-7258: name the parties here, not only on the Detail tab.
                  CR-4711 gave that tab a Customer field, but this modal has
                  ten tabs and the field is on one of them -- so opening GRN,
                  NCR or ASN/Shipments still left nothing on screen saying
                  whose order it was. The header is the only region that
                  survives a tab change, which is the same reason the PO
                  Details modal needed it. */}
              {lineParties && (
                <p
                  className="text-sm text-muted-foreground truncate"
                  title={lineParties}
                >
                  {lineParties}
                </p>
              )}
            </div>
            {/* EXPEDITING-PARITY PR 2 (Aaron, 17 Sep 2026): the separate On
                Hold chip is GONE. A held line's status IS the hold, so this
                header showed two badges that could contradict each other — the
                exact thing he asked us to stop. POLineHeaderBadges renders one
                status badge from `statusOf`, and the hold count rides beside it.

                Walkthrough batch 2: the ORDER is his, stated on the walk —
                status, then who owes the next move, then % delivered. It used
                to run the other way round, so the line's own state was the last
                thing read on a row of five chips. CRITICAL and the concession
                badge follow, keeping their own relative order (CR-6510). */}
            {/* CR-4704 / CR-4859 / CR-4882: action-ownership + acknowledge
                badges, now in POLineHeaderBadges. CR-8616: the concession
                badge is blank where the line's customer has concessions off. */}
            {line && (
              <POLineHeaderBadges line={line} holdCount={getChangeCount('on_hold')}>
                <ConcessionStatusBadge status={line.concession_status} customerId={line.customer_id} />
              </POLineHeaderBadges>
            )}
            {/* CR-7418 (Aaron, 19 Sep): *"the philosophy in the portal is
                statuses go at the top, actions go at the bottom"*. The OTIF
                decision is a STATUS, so it reads here with New / % Delivered /
                CRITICAL rather than down in the action bar where the first cut
                put it. Only a line that CARRIES a decision gets a badge — a
                line nobody has decided has no OTIF status to state, and a chip
                saying "Not Decided" on every delivered line in the tenant is
                the furniture this ticket exists to remove. Who / when / why
                rides the tooltip and is spelled out in the Audit History
                panel beside it.

                Both sides, same text (CR-6348, Aaron 5 Oct 2026): the supplier
                reads the decision, read only, and the who/when/why is the
                Audit History entry both sides already see. */}
            {line && (line.otif_overridden || line.otif_acknowledged) && (() => {
              const audit = otifDecisionAudit(detailAuditEntries, otifDecisionOf(line));
              const why = otifDecisionReason(audit);
              const title = audit
                ? `by ${audit.changed_by} on ${contextFormatDate(audit.change_date, { includeTime: true })}${why ? ` — ${why}` : ''}`
                : undefined;
              return <span title={title}><OtifDecisionBadge line={line} withPrefix /></span>;
            })()}
            {/* CR-4701: % Delivered in header.
                CR-7417: from `deliveredPercentOf`, so the pill, the grid cell
                and the grid's sort are one number. This pill used to compute
                received_qty/quantity and ROUND, while the grid cell computed
                the same ratio and FLOORED — two answers to one question, on top
                of both reading the wrong quantity.
                CR-7468: that one number is the DELIVERED quantity — what the
                receipts say arrived. Accepted has its own field on the Detail
                tab now, so this pill answers the question its label asks. */}
            {line && (() => {
              const pct = deliveredPercentOf(line);
              // No figure, no pill. A frozen row whose capture never held the
              // quantity has nothing to claim, and "0% Delivered" would be a
              // claim (CR-7417 rework round 1).
              if (pct === null) return null;
              const color = pct >= 100 ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : pct >= 50 ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-gray-100 text-gray-800 border-gray-300';
              return (
                <span className={`px-3 py-1 rounded-full font-semibold text-xs border ${color}`}>
                  {pct}% Delivered
                </span>
              );
            })()}
            {line?.is_critical && (
              <span className="px-3 py-1 rounded-full font-semibold text-xs bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                CRITICAL
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
              <p className="text-red-800">Error: {error}</p>
              <button onClick={() => fetchLineDetails()} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">Try again</button>
            </div>
          ) : line ? (
            <POLineDetailTabs
              line={line}
              lineId={lineId}
              userType={userType}
              activeTab={modalTab}
              onTabChange={setModalTab}
              detailContent={(
                <POLineDetailTabContent
                  line={line}
                  changelogs={detailAuditEntries}
                  userType={userType}
                  userId={user?.id}
                  formatCurrency={formatCurrency}
                  formatUnitPrice={formatLineUnitPrice}
                  formatDate={formatDate}
                  formatQuantity={formatQuantity}
                  getChangeCount={getChangeCount}
                  canShowWorkflowActions={canShowWorkflowActions()}
                  onReviewChanges={() => setModalTab('change-requests')}
                  usesScheduleLines={scheduleCollaborationActive}
                />
              )}
              changelogs={changelogs}
              grnCount={grnCount}
              ncrs={ncrs}
              asnCount={asnCount}
              unreadMessageCount={unreadMessageCount}
              onChangeRequestUpdate={async () => { await fetchLineDetails(); onUpdate?.(); }}
              onShortageUpdate={fetchLineDetails}
              onMessagesRead={refreshUnreadMessageCount}
            />
          ) : null}
        </div>

        {/* CR-7418: the OTIF bar STATES THE DECISION, and offers whatever
            actions the server still allows.

            CR-6190 put a single Override button here, gated on
            `can_override_otif` — which the server turns OFF the moment a line
            is decided. So the bar vanished exactly when it had something to
            say, and Sarah's ticket is the consequence: "nothing to tell you
            [the] po line [has] been overridden on the modal". A decided line
            now shows who decided it, when, and why, and a reviewer can reset
            or acknowledge from here rather than only override. */}
        {line && (line.can_override_otif || line.can_acknowledge_otif || line.can_reset_otif) && (
          <div className="border-t px-4 py-2 flex items-center justify-end gap-4 bg-muted/30">
            <OTIFReviewActions
              line={line}
              onDone={() => { fetchLineDetails(true); onUpdate?.(); }}
            />
          </div>
        )}

        {/* Footer — CR-4703: fixed action bar, always visible.
            CR-4705 round 2 rework: pass activeTab so the footer can hide
            the "Review Changes" button once the user is already on the
            Change Requests tab. */}
        {line && (
          <POLineDetailFooter
            line={line}
            activeTab={modalTab}
            canToggleCritical={line.status !== 'received' && userType !== 'supplier'}
            /* Walkthrough batch 2 (Aaron, 17 Sep 2026) — ONE expression was
               three of his findings:

               * `hasPurchasingWriteAccess` is a CUSTOMER site permission, so it
                 is false for every supplier. The supplier therefore had neither
                 Put On Hold nor Take Off Hold in this modal, on a line they can
                 hold from the grid.
               * It never asked WHOSE hold it is, so a customer looking at a hold
                 the supplier had taken still saw Take Off Hold — the control the
                 grid correctly withholds, and one the server refuses.

               The grid has had the right rule since G7 (`canReleaseHold`: the
               side the hold NAMES is the only side that releases it). Ask it
               here rather than restating it — two answers to one question is
               how these surfaces drifted apart in the first place. */
            canToggleHold={
              // ROUND 2: a line already on hold can always be RELEASED here,
              // including once it is received or cancelled. The old
              // `status !== 'received'` gated both directions, so a hold that
              // outlived its receipt could not be lifted from this modal
              // either — see POLineRowActions for the measured case. What the
              // status still forbids is putting a FINISHED line on hold.
              (line.on_hold || !lineIsFinished(line))
              && (userType === 'supplier' || hasPurchasingWriteAccess(line.customer_id))
              && (!line.on_hold || canReleaseHold(user, line))
            }
            /* And not BOTH. On a customer's `new` line, Propose Change asks
               the supplier to agree a date they have not yet been asked about,
               while Update Dates just sets it — two doors to one job, saying
               different things. Legacy has one dialog here for the same reason. */
            canShowProposeChange={canShowProposeChange() && !maySetDatesDirectly}
            /* The supplier answering a `new` order. canShowWorkflowActions
               already says when that is allowed; what was missing is a control
               to do it with — the footer's only workflow button routes to the
               Change Requests tab, and a `new` line has no change request, so
               the supplier was shown nothing at all. Same action as the grid's
               tick (accept-delivery-date), same modal. */
            canAcknowledge={
              userType === 'supplier'
              && line.acknowledge_status === 'new'
              && canShowWorkflowActions()
            }
            canSetDates={maySetDatesDirectly}
            // Customer review 11 Sep (item 28): "Review Changes" only when
            // there is a change to review. A supplier on a `new` line was sent
            // to an empty Change Requests tab (CR-7277: nothing to show,
            // nothing to click).
            canShowWorkflowActions={canShowWorkflowActions() && canActOnProposal(line)}
            isProposer={
              line.status !== 'received' && (
                (userType === 'supplier' && line.acknowledge_status === 'change_proposed_supplier') ||
                (userType === 'customer' && line.acknowledge_status === 'change_proposed_customer')
              )
            }
            // CR-5551: only suppliers raise — customers approve. Hide on
            // closed/received lines where a new deviation makes no sense.
            // CR-6385: and only where THIS line's customer has concessions on;
            // a supplier can serve one customer that runs the workflow and one
            // that doesn't, so the switch has to be read per line.
            canRaiseDeviation={
              userType === 'supplier'
              && line.status !== 'closed'
              && line.status !== 'received'
              && isConcessionEnabledForCustomer(line.customer_id)
            }
            onRaiseConcession={() => setDeviationRequestType('concession')}
            onRequestProductionPermit={() => setDeviationRequestType('production_permit')}
            onToggleCritical={handleToggleCritical}
            /* Round 1: gated like the other workflow actions -- not on a schedule-
               collaboration tenant, and a customer needs purchasing write access. */
            canCloseRequest={
              mayCloseDeclinedRequest(line, userType)
              && !scheduleCollaborationActive
              && (userType === 'supplier' || hasPurchasingWriteAccess(line.customer_id))
            }
            onCloseRequest={handleCloseDeclinedRequest}
            onAcknowledge={() => setAcceptModalOpen(true)}
            onSetDates={() => setSetDatesModalOpen(true)}
            onToggleHold={() => {
              if (line.on_hold) {
                handleToggleHold();
              } else {
                setHoldModalOpen(true);
              }
            }}
            onPropose={() => setProposeModalOpen(true)}
            onSwitchToChangeRequests={() => setModalTab('change-requests')}
            onClose={onClose}
          />
        )}
        {!line && (
          <div className="border-t p-4 flex justify-end gap-3 bg-muted/10">
            <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted font-medium">Close</button>
          </div>
        )}
      </div>

      {/* Workflow Modals — the wiring lives in POLineDetailDialogs, which is
          what took this file back under helix-code's 500-line cap (C3). */}
      {line && (
        <POLineDetailDialogs
          line={line}
          userType={userType}
          placerSide={placerSideOf(viewerSideOf(user, actingSide))}
          acceptOpen={acceptModalOpen}
          proposeOpen={proposeModalOpen}
          setDatesOpen={setDatesModalOpen}
          holdOpen={holdModalOpen}
          deviationRequestType={deviationRequestType}
          onCloseAccept={() => setAcceptModalOpen(false)}
          onClosePropose={() => setProposeModalOpen(false)}
          onCloseSetDates={() => setSetDatesModalOpen(false)}
          onCloseHold={() => setHoldModalOpen(false)}
          onCloseDeviation={() => setDeviationRequestType(null)}
          onWorkflowAction={handleWorkflowAction}
          onSetDates={handleSetDates}
          onHold={async (reason, nextAction) => { await handleToggleHold(reason, nextAction); }}
          onDeviationSuccess={async () => { await fetchLineDetails(); onUpdate?.(); }}
        />
      )}
    </div>
  );
}
